import React, { useEffect, useState } from "react";
import './login.css';
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AxiosResponse } from "axios";
import { LoginSuccessResponse } from "../../../types/api/login-success-response";
import { RoleEnum } from "../../../enums/role.enum";
import { GetMeSuccessResponse } from "../../../types/api/get-me-success-response";
import { networkCall } from "../../../../../utils/network-call/network-call";
import { login, logout, setMe } from "../../../../../redux/reducer/session.reducer";
import { isValidEmail } from "../../../../../utils/is-valid-email/is-valid-email";
import { showLoadingAlertMessage } from "../../../../../utils/show-loading-alert-message/show-loading-alert-message";
import { showDefaultErrorMessages } from "../../../../../utils/show-default-error-message/show-default-error-messages";
import { AlertMessageState, clearAlertMessage, showAlertMessage } from "../../../../../redux/reducer/alert-message.reducer";
import { AlertMessages } from "../../../../../enums/alert-messages.enum";
import { DefaultErrorResponse } from "../../../../../types/api/error/default-error-response/default-error-respose.type";
import { ValidationErrorResponseData } from "../../../../../types/api/error/validation-error-response-data/validation-error-response-data.type.";
import { getImageUrl } from "../../../../../utils/image-helper/get-image-url";
import { EmailInput } from "../../../../../components/field/email-input/email-input";
import { PasswordInput } from "../../../../../components/field/password-input/password-input";

export const Login = () => {
    const [formValues, setFormValues] = useState({
        email: {
            value: '',
            error: ''
        },
        password: {
            value: '',
            error: ''
        }
    });
    const [rememberMe, setRememberMe] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        const rememberMeInStorage = localStorage.getItem('RememberMe');
        const accessToken = localStorage.getItem('AccessToken');

        const forward = async () => {
            if (rememberMeInStorage === '1' && accessToken) {
                const response = await networkCall('/users/me');
                if (response?.status === 200) {
                    const res: AxiosResponse<GetMeSuccessResponse> = response;
                    dispatch(setMe({
                        user: res.data.user
                    }));
        
                    switch (res.data.user.role) {
                        case RoleEnum.Admin:
                            navigate('/cms/admin');
                            return;
                        case RoleEnum.CompanyAdmin:
                            navigate('/cms/company-admin');
                            return;
                        case RoleEnum.Maintainer:
                            navigate('/cms/maintainer');
                            return;
                        case RoleEnum.User:
                            navigate('/cms/user');
                            return;
                        default:
                    }
                }
            }

            dispatch(logout());
        };

        forward();
        
    }, [dispatch, navigate]);

    const handleChange = (name: keyof typeof formValues, value: string) => {
        const newFormValues: typeof formValues = JSON.parse(JSON.stringify(formValues));
        newFormValues[name].value = value;
        newFormValues[name].error = '';
        setFormValues(newFormValues);
    };

    const validateForm = () => {
        const newFormValues = JSON.parse(JSON.stringify(formValues));
        
        let valid = true;
        newFormValues.email.error = '';
        newFormValues.password.error = '';

        if (!formValues.email.value) {
            newFormValues.email.error = 'Mező kitöltése kötelező.';
            valid = false;
        }

        if (!formValues.password.value) {
            newFormValues.password.error = 'Mező kitöltése kötelező.';
            valid = false;
        }

        if (!valid) {
            setFormValues(newFormValues);
            return false;
        }

        if (isValidEmail(formValues.email.value)) {
            setFormValues(newFormValues);
            return true;
        }

        newFormValues.email.error = 'Helytelen email formátum.';
        setFormValues(newFormValues);
        return false;
    };

    const handleLoginClick = async () => {
        if (!validateForm()) return;

        showLoadingAlertMessage();

        const response = await networkCall('/users/login', {
            method: 'POST',
            body: {
                email: formValues.email.value,
                password: formValues.password.value
            }
        });

        const messageShowed = showDefaultErrorMessages(response);
        if(messageShowed) return;

        dispatch(clearAlertMessage());
        const alertMessageState: AlertMessageState = {
            alertMessageType: 'error',
            alertMessageText: AlertMessages.UnknownErrorTryAgain
        };

        if (response?.status === 200) {

            const res: AxiosResponse<LoginSuccessResponse> = response;

            dispatch(login({
                user: res.data.user,
                accessToken: res.data.accessToken,
                rememberMe
            }));

            switch (res.data.user.role) {
                case RoleEnum.Admin:
                    navigate('/cms/admin');
                    return;
                case RoleEnum.CompanyAdmin:
                    navigate('/cms/company-admin');
                    return;
                case RoleEnum.Maintainer:
                    navigate('/cms/maintainer');
                    return;
                case RoleEnum.User:
                    navigate('/cms/user');
                    return;
                default:
            }

            alertMessageState.alertMessageText = 'Hibás email cím vagy jelszó.';

        } else if (response?.status === 400) {
            const _res: AxiosResponse<DefaultErrorResponse> = response;

            switch (_res.data.code) {
                case 1000:
                    const res: AxiosResponse<DefaultErrorResponse<ValidationErrorResponseData[]>> = response;
                    alertMessageState.alertMessageText = 'HibáSan megadott beviteli mezők.';
                    if (res.data.data) {
                        const newFormValues = JSON.parse(JSON.stringify(formValues));
                        for (const element of res.data.data) {
                            if (element.field === 'email') newFormValues.email.error = element.errors.join(', ');
                            else if (element.field === 'password') newFormValues.password.error = element.errors.join(', ');
                        }
                        setFormValues(newFormValues);
                    }
                    break;
            }
        } else if (response?.status === 404) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {
                case 7000:
                    alertMessageState.alertMessageText = 'Hibás email cím vagy jelszó.';
                    break;
            }
        }

        dispatch(clearAlertMessage());
        dispatch(showAlertMessage(alertMessageState));
    };

    return (
        <div className="login-container">
            <div className="title">
                <img src={getImageUrl('holoverzum-icon64x64.png')} alt="holoverzum-logo" />
                Holoverzum
            </div>
            <div className="body">
                <EmailInput
                    id="email"
                    name="email"
                    value={formValues.email.value}
                    onChange={(value) => handleChange('email', value)}
                    placeholder="email cím"
                    icon={getImageUrl('user-icon.png')}
                    error={formValues.email.error}
                />
                <div style={{ height: '25px' }}/>
                <PasswordInput
                    id="password"
                    name="password"
                    value={formValues.password.value}
                    onChange={(value) => handleChange('password', value)}
                    onKeyDown={
                        (e) => {
                            if (e.key.toLowerCase() === 'enter') {
                                handleLoginClick();
                            }
                        }
                    }
                    placeholder="jelszó: holoverzum"
                    icon={getImageUrl('password-icon.png')}
                    error={formValues.password.error}
                />

                <div className="login-links">
                    <span className="remember-me">
                        <label>
                            <input type="checkbox" checked={rememberMe} onChange={() => setRememberMe(!rememberMe)} />
                            &nbsp;Emlékezz rám
                        </label>
                    </span>
                    <Link to='/forgotten-password' className="forgotten-password-link">Elfelejtett jelszó</Link>
                </div>

                <button className="login-btn" onClick={handleLoginClick}>Bejelentkezés</button>
            </div>
        </div>
    );
};